import "rsuite/dist/rsuite.css";
import React, { useState, useEffect } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Form, Container, Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DateRangePicker } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { clearSession } from '../AutoLogoutWrapper';

const formatNumber = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatQty = (n) => Number(n || 0).toLocaleString('en-US');

// { '$': 1000 } -> "$ 1,000.00"
const formatMoneyMap = (map) => {
    const parts = Object.keys(map || {}).filter(cur => map[cur]).map(cur => `${cur} ${formatNumber(map[cur])}`);
    return parts.length > 0 ? parts.join(' + ') : '-';
};

const formatUnixDate = (unixTime) => {
    if (!unixTime) return '-';
    return new Date(unixTime * 1000).toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric'
    }).replace(/\//g, '.');
};

const daysSince = (unixTime) => {
    if (!unixTime) return null;
    return Math.max(0, Math.floor((Date.now() / 1000 - unixTime) / 86400));
};

function InfoTip({ id, text }) {
    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id={id} className="pending-commercial-tip">{text}</Tooltip>}>
            <i className="fa fa-info-circle text-info" style={{ marginLeft: '5px', cursor: 'help' }}></i>
        </OverlayTrigger>
    );
}

const TIPS = {
    parties: 'Number of parties that have at least one Proforma Invoice (PI) for which no commercial has been made yet.',
    pis: 'Total number of approved PIs, across all parties, that are not yet included in any commercial.',
    value: 'Sum of the value of all these PIs. The value is calculated the same way as on the Manage Orders page (from the order lines). Only dollar ($) orders are counted.',
    ready: 'PIs that are ready for a commercial. A PI is ready only when every order line in it has been delivered in full (delivered quantity is equal to or more than ordered quantity). If even one piece is still pending, the PI is not ready. The value shown is the total value of the ready PIs.',
    noPis: 'Number of PIs of this party for which no commercial has been made yet.',
    piNumbers: 'PI number of each pending PI, with the total value of that PI in brackets.',
    totalValue: 'Total value of all the pending PIs of this party, calculated the same way as on the Manage Orders page.',
    readyToBill: 'Number of this party\'s pending PIs that are fully delivered (every order line delivered in full), out of all its pending PIs. Only these can be made into a commercial right now.',
    oldestPi: 'The oldest PI of this party that still has no commercial, with its date.',
    daysPending: 'Number of days since the oldest pending PI of this party was created (today minus the PI date). The higher the number, the longer this party has been waiting to be billed.'
};

function PendingCommercial() {
    const navigate = useNavigate();
    const [parties, setParties] = useState([]);
    const [summary, setSummary] = useState({ partyCount: 0, piCount: 0, totalValue: {}, readyCount: 0, readyValue: {} });
    const [partyOptions, setPartyOptions] = useState([]);
    const [filters, setFilters] = useState({ partyname: '' });
    // Default: 1 May 2026 to tomorrow (one day ahead so the current day is never cut off by a timezone difference).
    // Older PIs are not on record, so they are hidden unless the user clears or changes the date range.
    const [dateRange, setDateRange] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        return [new Date(2026, 4, 1), tomorrow];
    });
    const [expanded, setExpanded] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        axios.get(`${window.backendUrl}/get_all_parties`)
            .then(res => setPartyOptions(res.data.data.map(p => p.PartyName)))
            .catch(error => console.error('Error fetching parties:', error));
    }, []);

    useEffect(() => {
        setLoading(true);
        axios.get(`${window.backendUrl}/get_pending_commercial`, {
            params: {
                partyname: filters.partyname,
                startDate: dateRange[0] || '',
                endDate: dateRange[1] || ''
            },
            withCredentials: true
        })
        .then((response) => {
            setParties(response.data.data || []);
            setSummary(response.data.summary || { partyCount: 0, piCount: 0, totalValue: {}, readyCount: 0, readyValue: {} });
        })
        .catch((error) => {
            if (error.response?.status === 401) {
                clearSession(); // keeps the AI Office preferences
                navigate('/');
            } else {
                console.error(error);
            }
        })
        .finally(() => setLoading(false));
    }, [filters, dateRange, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleDateRangeChange = (value) => {
        setDateRange(value && value.length === 2 ? value : []);
    };

    const toggleParty = (partyName) => {
        setExpanded(prev => ({ ...prev, [partyName]: !prev[partyName] }));
    };

    const statusOf = (pi) => {
        if (pi.ready) return 'Ready to bill';
        if (pi.deliveredQty > 0) return 'Partly delivered';
        return 'Not delivered';
    };

    const thStyle = { backgroundColor: '#d9edf7' };

    const renderDetail = (party) => {
        // Totals of the whole party, kept per currency so $ and ৳ are never added together
        const totalQty = party.pis.reduce((s, p) => s + p.totalQty, 0);
        const deliveredQty = party.pis.reduce((s, p) => s + p.deliveredQty, 0);
        const balanceQty = party.pis.reduce((s, p) => s + p.balanceQty, 0);
        const sumBy = (field) => party.pis.reduce((acc, p) => {
            acc[p.currency] = (acc[p.currency] || 0) + p[field];
            return acc;
        }, {});

        return (
            <Table bordered hover responsive style={{ fontSize: '12px', backgroundColor: '#fff', marginBottom: 0 }}>
                <thead style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    <tr>
                        <th style={thStyle}>Party Name</th>
                        <th style={thStyle}>PI No.</th>
                        <th style={thStyle}>PI Date</th>
                        <th style={thStyle}>Delivery Date</th>
                        <th style={thStyle}>Total Qty</th>
                        <th style={thStyle}>Delivered Qty</th>
                        <th style={thStyle}>Balance Qty</th>
                        <th style={thStyle}>Delivered %</th>
                        <th style={thStyle}>Total Value</th>
                        <th style={thStyle}>Delivered Value</th>
                        <th style={thStyle}>Balance Value</th>
                        <th style={thStyle}>Days Pending</th>
                        <th style={thStyle}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {party.pis.map(pi => (
                        <tr key={pi.piId} style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                            <td>{pi.partyName}</td>
                            <td>{pi.piNo}</td>
                            <td>{formatUnixDate(pi.piDate)}</td>
                            <td>{formatUnixDate(pi.deliveryDate)}</td>
                            <td>{formatQty(pi.totalQty)}</td>
                            <td>{formatQty(pi.deliveredQty)}</td>
                            <td>{formatQty(pi.balanceQty)}</td>
                            <td>{pi.deliveryPercent}%</td>
                            <td>{pi.currency} {formatNumber(pi.totalValue)}</td>
                            <td>{pi.currency} {formatNumber(pi.deliveredValue)}</td>
                            <td>{pi.currency} {formatNumber(pi.balanceValue)}</td>
                            <td>{daysSince(pi.piDate) ?? '-'}</td>
                            <td><strong>{statusOf(pi)}</strong></td>
                        </tr>
                    ))}
                    <tr style={{ verticalAlign: 'middle', textAlign: 'center', fontWeight: 'bold' }}>
                        <td colSpan="4" className="text-end">Total ({party.pis.length} PIs)</td>
                        <td>{formatQty(totalQty)}</td>
                        <td>{formatQty(deliveredQty)}</td>
                        <td>{formatQty(balanceQty)}</td>
                        <td>{totalQty > 0 ? Math.min(100, Math.round((deliveredQty / totalQty) * 100)) : 0}%</td>
                        <td>{formatMoneyMap(sumBy('totalValue'))}</td>
                        <td>{formatMoneyMap(sumBy('deliveredValue'))}</td>
                        <td>{formatMoneyMap(sumBy('balanceValue'))}</td>
                        <td colSpan="2"></td>
                    </tr>
                </tbody>
            </Table>
        );
    };

    return (
        <div style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
            <style>{'.pending-commercial-tip .tooltip-inner { max-width: 320px; text-align: left; }'}</style>
            <NavigationBar />
            <Container fluid style={{ paddingBottom: '20px' }}>
                <Row style={{ marginTop: '20px', marginBottom: '10px', alignItems: 'center' }}>
                    <Col md={6} className="d-flex align-items-center gap-2">
                        <h4 style={{ margin: 0 }}>Pending Commercial</h4>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form className="d-flex justify-content-start gap-3 w-100 align-items-center">
                            <div style={{ flex: '1', maxWidth: '220px' }}>
                                <DateRangePicker placeholder="PI date: From to Todate" value={dateRange.length === 2 ? dateRange : null} onChange={handleDateRangeChange} style={{ width: '100%' }} />
                            </div>
                            <Form.Select style={{ flex: '1', maxWidth: '220px' }} name="partyname" value={filters.partyname} onChange={handleInputChange}>
                                <option value="">All Party</option>
                                {partyOptions.map((p, i) => <option key={i} value={p}>{p}</option>)}
                            </Form.Select>
                        </Form>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <div className="d-flex gap-3" style={{ fontSize: '12px' }}>
                            <div style={{ border: '1px solid #dee2e6', padding: '8px 16px', minWidth: '170px' }}>
                                <div>Parties with pending PIs<InfoTip id="tip-parties" text={TIPS.parties} /></div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{summary.partyCount}</div>
                            </div>
                            <div style={{ border: '1px solid #dee2e6', padding: '8px 16px', minWidth: '170px' }}>
                                <div>Total pending PIs<InfoTip id="tip-pis" text={TIPS.pis} /></div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{summary.piCount}</div>
                            </div>
                            <div style={{ border: '1px solid #dee2e6', padding: '8px 16px', minWidth: '170px' }}>
                                <div>Total unbilled value<InfoTip id="tip-value" text={TIPS.value} /></div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{formatMoneyMap(summary.totalValue)}</div>
                            </div>
                            <div style={{ border: '1px solid #dee2e6', padding: '8px 16px', minWidth: '170px' }}>
                                <div>Ready to bill now<InfoTip id="tip-ready" text={TIPS.ready} /></div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{summary.readyCount} PIs &nbsp;|&nbsp; {formatMoneyMap(summary.readyValue)}</div>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Table bordered hover responsive style={{ fontSize: '12px', backgroundColor: '#fff', border: '1px solid #dee2e6' }}>
                            <thead style={{ backgroundColor: '#d9edf7', textAlign: 'center', verticalAlign: 'middle' }}>
                                <tr>
                                    <th>Party Name</th>
                                    <th>No. PIs Pending<InfoTip id="tip-nopis" text={TIPS.noPis} /></th>
                                    <th>PI Numbers<InfoTip id="tip-pinumbers" text={TIPS.piNumbers} /></th>
                                    <th>Total Value<InfoTip id="tip-totalvalue" text={TIPS.totalValue} /></th>
                                    <th>Ready to Bill<InfoTip id="tip-readytobill" text={TIPS.readyToBill} /></th>
                                    <th>Oldest PI<InfoTip id="tip-oldestpi" text={TIPS.oldestPi} /></th>
                                    <th>Days Pending<InfoTip id="tip-days" text={TIPS.daysPending} /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {parties.length > 0 ? parties.map((party) => (
                                    <React.Fragment key={party.partyName}>
                                        <tr
                                            onClick={() => toggleParty(party.partyName)}
                                            style={{ verticalAlign: 'middle', textAlign: 'center', cursor: 'pointer' }}>
                                            <td>
                                                <i className={`fa ${expanded[party.partyName] ? 'fa-caret-down' : 'fa-caret-right'}`} style={{ marginRight: '8px' }}></i>
                                                {party.partyName}
                                            </td>
                                            <td>{party.piCount}</td>
                                            <td style={{ fontSize: '11px', textAlign: 'left', width: '300px' }}>
                                                {party.pis.map(pi => `${pi.piNo} (${pi.currency} ${formatNumber(pi.totalValue)})`).join(', ')}
                                            </td>
                                            <td>{formatMoneyMap(party.totalValue)}</td>
                                            <td>{party.readyCount} of {party.piCount}</td>
                                            <td>
                                                <div>{party.oldestPiNo}</div>
                                                <div>{formatUnixDate(party.oldestPiDate)}</div>
                                            </td>
                                            <td>{party.daysPending ?? '-'}</td>
                                        </tr>
                                        {expanded[party.partyName] && (
                                            <tr>
                                                <td colSpan="7" style={{ padding: '10px' }}>
                                                    {renderDetail(party)}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4 text-muted">
                                            {loading ? 'Loading...' : 'No pending PIs found.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default PendingCommercial;
